/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// ============================================================================
// PRODUCT SIZE SELECTION SYNCHRONIZATION & CARD HOVER REDIRECT
// ============================================================================
(function() {

  function syncSizeSelection(productId, sizeValue, sourceElement, isInit) {
    if (!productId || !sizeValue) {
      return;
    }

    const normalizedSize = sizeValue.trim().toLowerCase();

    // 1. Sync any <variant-picker> for this product (PDP & Quick View)
    const variantPickers = document.querySelectorAll(`variant-picker[data-product-id="${productId}"]`);
    variantPickers.forEach((picker, index) => {
      if (picker === sourceElement) {
        return;
      }

      // Try finding standard radio input label matching sizeValue
      const labels = Array.from(picker.querySelectorAll('label'));
      const matchingLabel = labels.find(lbl => lbl.textContent.trim().toLowerCase() === normalizedSize);
      if (matchingLabel) {
        const inputId = matchingLabel.getAttribute('for');
        const input = picker.querySelector(`#${CSS.escape(inputId)}`);
        if (input && !input.checked) {
          input.checked = true;
          if (!isInit) {
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      // Try finding dropdown combo-box button matching sizeValue
      const comboButtons = Array.from(picker.querySelectorAll('.combo-box__option-item'));
      const matchingComboBtn = comboButtons.find(btn => btn.textContent.trim().toLowerCase() === normalizedSize);
      if (matchingComboBtn) {
        if (matchingComboBtn.getAttribute('aria-selected') !== 'true') {
          if (isInit) {
            matchingComboBtn.setAttribute('aria-selected', 'true');
            const selectValueSpan = picker.querySelector('.select__selected-value');
            if (selectValueSpan) selectValueSpan.textContent = matchingComboBtn.textContent.trim();
            const hiddenInput = picker.querySelector('input[type="hidden"][data-option-position]');
            if (hiddenInput) hiddenInput.value = matchingComboBtn.getAttribute('value') || hiddenInput.value;
          } else {
            matchingComboBtn.click();
          }
        }
      }
    });

    // 2. Sync any FBT (Frequently Bought Together) drawer item dropdowns
    const fbtDrawerItems = document.querySelectorAll(`.fbt-drawer-item[data-product-id="${productId}"]`);
    fbtDrawerItems.forEach((item, index) => {
      const select = item.querySelector('.fbt-drawer-item__select');
      if (select && select !== sourceElement) {
        const option = Array.from(select.options).find(opt => {
          const optText = opt.text.trim().toLowerCase();
          return optText === normalizedSize || optText.startsWith(normalizedSize + ' ') || optText.startsWith(normalizedSize + ' -');
        });
        if (option && select.value !== option.value) {
          select.value = option.value;
          if (!isInit) {
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    });

    // 3. Sync any product card hover selectors
    const productCards = document.querySelectorAll(`product-item[data-product-id="${productId}"]`);
    productCards.forEach((card, index) => {
      // Sync old buttons if any exist
      const buttons = card.querySelectorAll('.product-item__size-hover-btn');
      buttons.forEach(btn => {
        const btnSize = btn.getAttribute('data-size-value');
        if (btnSize && btnSize.trim().toLowerCase() === normalizedSize) {
          btn.classList.add('is-selected');
        } else {
          btn.classList.remove('is-selected');
        }
      });

      // Sync new radio inputs
      const radios = card.querySelectorAll('.product-item__size-radio');
      radios.forEach(radio => {
        const radioSize = radio.value;
        const label = radio.closest('.product-item__size-radio-label');
        if (radioSize && radioSize.trim().toLowerCase() === normalizedSize) {
          radio.checked = true;
          if (label) label.classList.add('is-selected');
        } else {
          radio.checked = false;
          if (label) label.classList.remove('is-selected');
        }
      });
    });
  }

  // Helper to extract selected size name from a variant-picker
  function getSelectedSizeName(picker) {
    // 1. Check for standard checked radio buttons (highest priority)
    const checkedInput = picker.querySelector('input[data-option-position][type="radio"]:checked');
    if (checkedInput) {
      const label = picker.querySelector(`label[for="${checkedInput.id}"]`);
      if (label) {
        return label.textContent.trim();
      }
    }

    // 2. Check for active link swatches (for options rendering as <a> links, e.g. block swatches with URLs)
    const activeLink = picker.querySelector('.block-swatch__item.is-selected, .variant-swatch__item.is-selected, .color-swatch__item.is-selected');
    if (activeLink) {
      return activeLink.textContent.trim();
    }

    // 3. Check for select dropdowns
    const select = picker.querySelector('select[data-option-position]');
    if (select && select.selectedIndex >= 0) {
      return select.options[select.selectedIndex].text.trim();
    }

    // 4. Check for combo-box selected value indicator span
    const selectValueSpan = picker.querySelector('.select__selected-value');
    if (selectValueSpan) {
      return selectValueSpan.textContent.trim();
    }

    // 5. Fallback: Check the server-rendered selected option value display span for Size
    const optionInfos = Array.from(picker.querySelectorAll('.product-form__option-info'));
    const sizeInfo = optionInfos.find(info => {
      const name = info.querySelector('.product-form__option-name');
      return name && name.textContent.toLowerCase().includes('size');
    });
    if (sizeInfo) {
      const valSpan = sizeInfo.querySelector('.product-form__option-value');
      if (valSpan) {
        return valSpan.textContent.trim();
      }
    }

    return null;
  }

  // Listen to variantpicker option changes
  document.addEventListener('change', function(event) {
    const picker = event.target.closest('variant-picker');
    if (picker) {
      const productId = picker.getAttribute('data-product-id');
      const sizeValue = getSelectedSizeName(picker);
      if (productId && sizeValue) {
        syncSizeSelection(productId, sizeValue, picker);
      }
    }
  });

  // Listen to theme's variant:changed custom event (handles PDP / Quick View loads & ajax combinations)
  document.addEventListener('variant:changed', function(event) {
    const variant = event.detail.variant;
    const form = event.target;
    if (!variant || !form) return;
    
    const picker = form.closest('variant-picker') || document.querySelector(`variant-picker[form-id="${form.id}"]`);
    if (picker) {
      const productId = picker.getAttribute('data-product-id');
      const sizeValue = getSelectedSizeName(picker);
      if (productId && sizeValue) {
        syncSizeSelection(productId, sizeValue, picker);
      }
    }
  });

  // Listen to FBT select changes
  document.addEventListener('change', function(event) {
    const select = event.target.closest('.fbt-drawer-item__select');
    if (select) {
      const drawerItem = select.closest('.fbt-drawer-item');
      const productId = drawerItem?.getAttribute('data-product-id');
      const selectedOptionText = select.options[select.selectedIndex].text;
      const sizeValue = selectedOptionText.split(' - ')[0].split(' (')[0].trim();
      syncSizeSelection(productId, sizeValue, select);
    }
  });

  // Listen to product card hover sizes click to select and redirect (old button list)
  document.addEventListener('click', function(event) {
    const btn = event.target.closest('.product-item__size-hover-btn');
    if (btn) {
      event.preventDefault();
      event.stopPropagation();
      const productId = btn.getAttribute('data-product-id');
      const sizeValue = btn.getAttribute('data-size-value');
      const variantUrl = btn.getAttribute('data-variant-url');
      
      // Update UI selection locally
      syncSizeSelection(productId, sizeValue, btn);

      // Perform redirect to product page with selected variant preselected
      if (variantUrl) {
        window.location.href = variantUrl;
      } else {
        console.warn("Antigravity: No variant URL found on button!");
      }
    }
  });

  // Listen to product card hover sizes radio change to select and redirect (new radio list)
  document.addEventListener('change', function(event) {
    const radio = event.target.closest('.product-item__size-radio');
    if (radio && radio.checked) {
      const productId = radio.getAttribute('data-product-id');
      const sizeValue = radio.value;
      const variantUrl = radio.getAttribute('data-variant-url');
      
      // Update UI selection locally
      syncSizeSelection(productId, sizeValue, radio);

      // Perform redirect to product page with selected variant preselected
      if (variantUrl) {
        window.location.href = variantUrl;
      } else {
        console.warn("Antigravity: No variant URL found on radio!");
      }
    }
  });

  // Run initial synchronization on page load
  function initSyncOnLoad() {
    const mainPicker = document.querySelector('variant-picker');
    if (mainPicker) {
      const productId = mainPicker.getAttribute('data-product-id');
      const sizeValue = getSelectedSizeName(mainPicker);
      if (productId && sizeValue) {
        syncSizeSelection(productId, sizeValue, mainPicker, true);
      }
    }
  }

  function scheduleInitSync() {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(initSyncOnLoad, { timeout: 2000 });
    } else {
      window.requestAnimationFrame(initSyncOnLoad);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSyncOnLoad);
  } else {
    initSyncOnLoad();
  }
})();